import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { Smartphone, Tablet, AlertTriangle, AlertCircle, Code2, Eye, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DeviceCanvas, type SelectionPath } from "./DeviceCanvas";
import { AppLibraryPanel, type LibraryEntry } from "./AppLibraryPanel";
import { FolderModal } from "./FolderModal";
import { JsonView } from "./JsonView";
import { ExportSummaryPanel } from "./ExportSummaryPanel";
import { DropRulesPanel } from "./DropRulesPanel";
import { ImportLayoutDialog } from "./ImportLayoutDialog";
import { backfillIconKindByUrl } from "./iconKindBackfill";
import {
  type FormFactor,
  type HomeScreenConfig,
  type HomeScreenLayout,
  type IconItem,
  type IconKind,
  type Target,
  pageCapacity,
} from "./types";
import {
  buildConfig,
  getVariant,
  readExtension,
  setTarget,
  setVariant,
} from "./projection";
import { summarize, validateLayout } from "./validation";
import { cn } from "@/lib/utils";
import { getFaviconUrl } from "@/utils/favicon";

interface Props {
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}

// Legacy autosave purge lives in its own module so it can be unit-tested
// without spinning up the full editor (which pulls in dnd-kit, validation,
// and a deep tree of payload utilities). See `legacyDraftPurge.ts`.
import { purgeLegacyDrafts } from "./legacyDraftPurge";

export function HomeScreenLayoutEditor({ value, onChange }: Props) {
  // Wipe any legacy autosave drafts on first mount so previously-saved
  // arrangements do not unexpectedly hydrate over the server snapshot.
  useEffect(() => {
    purgeLegacyDrafts();
  }, []);


  const ext = useMemo(() => readExtension(value as unknown as HomeScreenConfig), [value]);
  const target: Target = ext.target;

  const [activeFf, setActiveFf] = useState<FormFactor>(
    target === "ipad" ? "ipad" : "iphone"
  );
  const [pageIndex, setPageIndex] = useState(0);
  const [selected, setSelected] = useState<SelectionPath | null>(null);
  const [openFolderIdx, setOpenFolderIdx] = useState<number | null>(null);
  const [view, setView] = useState<"design" | "json">("design");
  const [importOpen, setImportOpen] = useState(false);

  const layout = getVariant(ext, activeFf);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const writeLayout = useCallback(
    (ff: FormFactor, next: HomeScreenLayout) => {
      const updated = setVariant(ext, ff, next);
      onChange(buildConfig(updated) as unknown as Record<string, unknown>);
    },
    [ext, onChange]
  );

  // Backfill the persisted IconKind for older Web Clips that pre-date the
  // verdict field. The probe inside `WebClipFavicon` fires this once per
  // unresolved clip — and on first mount many clips can resolve in the same
  // tick, which previously produced one parent onChange per clip (each one
  // re-rendering the whole canvas and re-triggering favicon probes).
  //
  // We coalesce all verdicts that arrive in the same tick into a single
  // batched onChange applied against the latest value via a microtask. This
  // turns N partial-payload writes into one. `backfillIconKindByUrl`
  // returns null when nothing changed, so duplicates are no-ops.
  const valueRef = useRef(value);
  valueRef.current = value;
  const pendingKindResolutions = useRef<Array<{ url: string; kind: IconKind }>>([]);
  const flushScheduled = useRef(false);
  const handleWebClipIconKindResolved = useCallback(
    (url: string, kind: IconKind) => {
      pendingKindResolutions.current.push({ url, kind });
      if (flushScheduled.current) return;
      flushScheduled.current = true;
      queueMicrotask(() => {
        flushScheduled.current = false;
        const queued = pendingKindResolutions.current;
        pendingKindResolutions.current = [];
        if (queued.length === 0) return;
        let cfg = valueRef.current;
        let changed = false;
        for (const { url: u, kind: k } of queued) {
          const next = backfillIconKindByUrl(cfg, u, k);
          if (next) {
            cfg = next;
            changed = true;
          }
        }
        if (changed) onChange(cfg);
      });
    },
    [onChange]
  );

  function changeTarget(t: Target) {
    const updated = setTarget(ext, t);
    onChange(buildConfig(updated) as unknown as Record<string, unknown>);
    if (t === "ipad") setActiveFf("ipad");
    if (t === "iphone") setActiveFf("iphone");
  }

  function handleExport() {
    try {
      const json = JSON.stringify(value, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `home-screen-layout-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Layout exported");
    } catch {
      toast.error("Failed to export layout");
    }
  }

  function addItemToActive(item: IconItem) {
    const cap = pageCapacity(activeFf);
    const pages = layout.Pages.length ? [...layout.Pages] : [[]];
    const targetPage = [...(pages[pageIndex] ?? [])];
    if (targetPage.length >= cap) {
      // Spill to next page (creating one if needed)
      const nextIdx = pageIndex + 1;
      if (!pages[nextIdx]) pages.push([]);
      pages[nextIdx] = [...pages[nextIdx], item];
      writeLayout(activeFf, { ...layout, Pages: pages });
      setPageIndex(nextIdx);
      return;
    }
    targetPage.push(item);
    pages[pageIndex] = targetPage;
    writeLayout(activeFf, { ...layout, Pages: pages });
  }

  function placeAt(item: IconItem, dest: { kind: "page"; pageIndex: number; itemIndex: number } | { kind: "dock"; itemIndex: number }) {
    if (dest.kind === "dock") {
      // Insert and shift existing dock icons; spill any overflow.
      const dock = [...(layout.Dock ?? [])];
      const insertAt = Math.min(dest.itemIndex, dock.length);
      dock.splice(insertAt, 0, item);
      const dockMax = activeFf === "iphone" ? 4 : 6;
      const overflow = dock.splice(dockMax);
      const pages = layout.Pages.map((p) => [...p]);
      // Push any displaced dock icons to the first page that has room.
      if (overflow.length) {
        const cap = pageCapacity(activeFf);
        for (const ov of overflow) {
          let placed = false;
          for (let i = 0; i < pages.length; i++) {
            if (pages[i].length < cap) {
              pages[i].push(ov);
              placed = true;
              break;
            }
          }
          if (!placed) pages.push([ov]);
        }
      }
      writeLayout(activeFf, { ...layout, Dock: dock, Pages: pages });
      return;
    }

    const cap = pageCapacity(activeFf);
    const pages = layout.Pages.map((p) => [...p]);
    while (pages.length <= dest.pageIndex) pages.push([]);
    const page = pages[dest.pageIndex];
    // Snap insertion index into the contiguous range — never leave holes.
    const insertAt = Math.min(Math.max(0, dest.itemIndex), page.length);
    page.splice(insertAt, 0, item);

    // If the page now overflows, spill the tail into subsequent pages.
    let spillFrom = dest.pageIndex;
    while (pages[spillFrom].length > cap) {
      const tail = pages[spillFrom].splice(cap);
      spillFrom += 1;
      if (!pages[spillFrom]) pages[spillFrom] = [];
      pages[spillFrom] = [...tail, ...pages[spillFrom]];
    }
    writeLayout(activeFf, { ...layout, Pages: pages });
  }

  function removeSelected() {
    if (!selected) return;
    if (selected.kind === "dock") {
      const dock = (layout.Dock ?? []).filter((_, i) => i !== selected.itemIndex);
      writeLayout(activeFf, { ...layout, Dock: dock });
    } else {
      const pages = layout.Pages.map((p, i) =>
        i === selected.pageIndex ? p.filter((_, j) => j !== selected.itemIndex) : p
      );
      writeLayout(activeFf, { ...layout, Pages: pages });
    }
    setSelected(null);
  }

  function addPage() {
    const pages = [...layout.Pages, []];
    writeLayout(activeFf, { ...layout, Pages: pages });
    setPageIndex(pages.length - 1);
  }

  function removePage() {
    if (layout.Pages.length <= 1) return;
    if ((layout.Pages[pageIndex] ?? []).length > 0) return;
    const pages = layout.Pages.filter((_, i) => i !== pageIndex);
    writeLayout(activeFf, { ...layout, Pages: pages });
    setPageIndex(Math.max(0, pageIndex - 1));
  }

  function makeFolderFromSelection() {
    if (!selected || selected.kind !== "page") return;
    const item = layout.Pages[selected.pageIndex]?.[selected.itemIndex];
    if (!item || item.Type === "Folder") return;
    const folder: IconItem = {
      Type: "Folder",
      DisplayName: "New folder",
      Pages: [[item]],
    };
    const pages = layout.Pages.map((p, i) =>
      i === selected.pageIndex ? p.map((x, j) => (j === selected.itemIndex ? folder : x)) : p
    );
    writeLayout(activeFf, { ...layout, Pages: pages });
  }

  function parseDest(
    overId: string
  ): { kind: "page"; pageIndex: number; itemIndex: number } | { kind: "dock"; itemIndex: number } | null {
    if (overId.startsWith("cell:dock:")) {
      return { kind: "dock", itemIndex: Number(overId.split(":")[2]) };
    }
    if (overId.startsWith("cell:page:")) {
      const [, , p, c] = overId.split(":");
      return { kind: "page", pageIndex: Number(p), itemIndex: Number(c) };
    }
    return null;
  }

  function moveItem(
    from: SelectionPath,
    dest: { kind: "page"; pageIndex: number; itemIndex: number } | { kind: "dock"; itemIndex: number }
  ) {
    // No-op if dropped on the source cell.
    if (
      from.kind === dest.kind &&
      from.itemIndex === dest.itemIndex &&
      (from.kind === "dock" || from.pageIndex === (dest as { pageIndex: number }).pageIndex)
    ) {
      return;
    }

    // Snapshot current layout.
    const pages = layout.Pages.map((p) => [...p]);
    const dock = [...(layout.Dock ?? [])];

    // Pluck source item.
    let moved: IconItem | undefined;
    if (from.kind === "dock") {
      moved = dock[from.itemIndex];
      if (!moved) return;
      dock.splice(from.itemIndex, 1);
    } else {
      const srcPage = pages[from.pageIndex];
      if (!srcPage) return;
      moved = srcPage[from.itemIndex];
      if (!moved) return;
      srcPage.splice(from.itemIndex, 1);
    }

    let nextSelected: SelectionPath | null = null;

    if (dest.kind === "dock") {
      const dockMax = activeFf === "iphone" ? 4 : 6;
      // Adjust insertion index if removing from the dock shifted positions.
      let insertAt = Math.min(dest.itemIndex, dock.length);
      if (from.kind === "dock" && from.itemIndex < dest.itemIndex) insertAt -= 1;
      insertAt = Math.max(0, Math.min(insertAt, dock.length));
      dock.splice(insertAt, 0, moved);
      // Spill any overflow back to pages.
      const overflow = dock.splice(dockMax);
      if (overflow.length) {
        const cap = pageCapacity(activeFf);
        for (const ov of overflow) {
          let placed = false;
          for (let i = 0; i < pages.length; i++) {
            if (pages[i].length < cap) {
              pages[i].push(ov);
              placed = true;
              break;
            }
          }
          if (!placed) pages.push([ov]);
        }
      }
      nextSelected = { kind: "dock", itemIndex: Math.min(insertAt, dock.length - 1) };
    } else {
      while (pages.length <= dest.pageIndex) pages.push([]);
      const destPage = pages[dest.pageIndex];
      let insertAt = Math.min(Math.max(0, dest.itemIndex), destPage.length);
      if (
        from.kind === "page" &&
        from.pageIndex === dest.pageIndex &&
        from.itemIndex < dest.itemIndex
      ) {
        insertAt -= 1;
      }
      insertAt = Math.max(0, Math.min(insertAt, destPage.length));
      destPage.splice(insertAt, 0, moved);

      // Spill overflow from destination forward.
      const cap = pageCapacity(activeFf);
      let spillFrom = dest.pageIndex;
      while (pages[spillFrom].length > cap) {
        const tail = pages[spillFrom].splice(cap);
        spillFrom += 1;
        if (!pages[spillFrom]) pages[spillFrom] = [];
        pages[spillFrom] = [...tail, ...pages[spillFrom]];
      }
      nextSelected = { kind: "page", pageIndex: dest.pageIndex, itemIndex: insertAt };
    }

    writeLayout(activeFf, { ...layout, Pages: pages, Dock: dock });
    setSelected(nextSelected);
    if (nextSelected?.kind === "page") setPageIndex(nextSelected.pageIndex);
  }

  function handleDragEnd(e: DragEndEvent) {
    const overId = e.over?.id;
    if (!overId || typeof overId !== "string") return;
    const data = e.active.data.current as
      | { kind?: string; entry?: LibraryEntry; from?: SelectionPath }
      | undefined;
    if (!data) return;

    const dest = parseDest(overId);
    if (!dest) return;

    if (data.kind === "move" && data.from) {
      moveItem(data.from, dest);
      return;
    }

    if (data.kind === "library" && data.entry) {
      const entry = data.entry;
      const item: IconItem = entry.url
        ? {
            Type: "WebClip",
            URL: entry.url,
            DisplayName: entry.name,
            Icon: entry.iconUrl ?? getFaviconUrl(entry.url) ?? undefined,
            // Preserve the persisted favicon-vs-generic verdict from the
            // suggestion so the canvas tile renders correctly without a
            // fresh probe (covers re-add of a previously seen clip).
            IconKind: entry.iconKind,
          }
        : { Type: "Application", BundleID: entry.bundleId!, DisplayName: entry.name };
      placeAt(item, dest);
    }
  }

  // Validation
  const iphoneIssues = useMemo(
    () =>
      target === "iphone" || target === "both"
        ? validateLayout(getVariant(ext, "iphone"), "iphone")
        : [],
    [ext, target]
  );
  const ipadIssues = useMemo(
    () =>
      target === "ipad" || target === "both"
        ? validateLayout(getVariant(ext, "ipad"), "ipad")
        : [],
    [ext, target]
  );
  const issues = useMemo(
    () => [...iphoneIssues, ...ipadIssues],
    [iphoneIssues, ipadIssues]
  );
  const summary = summarize(issues);
  const issuesByFf = useMemo(
    () => ({
      iphone: summarize(iphoneIssues),
      ipad: summarize(ipadIssues),
    }),
    [iphoneIssues, ipadIssues]
  );

  const folder =
    openFolderIdx !== null && layout.Pages[pageIndex]?.[openFolderIdx]?.Type === "Folder"
      ? (layout.Pages[pageIndex][openFolderIdx] as Extract<IconItem, { Type: "Folder" }>)
      : null;

  return (
    <div className="space-y-4">
      <div className="border-b pb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-medium">Home Screen Layout</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Visually arrange apps, web clips, and folders. Choose a target device or design for both.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setImportOpen(true)}
            className="h-8 gap-1 text-xs"
          >
            <Upload className="h-3.5 w-3.5" /> Import layout
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleExport}
            className="h-8 gap-1 text-xs"
          >
            <Download className="h-3.5 w-3.5" /> Export layout
          </Button>
          <Tabs value={view} onValueChange={(v) => setView(v as "design" | "json")}>
            <TabsList className="h-8">
              <TabsTrigger value="design" className="text-xs gap-1">
                <Eye className="h-3.5 w-3.5" /> Design
              </TabsTrigger>
              <TabsTrigger value="json" className="text-xs gap-1">
                <Code2 className="h-3.5 w-3.5" /> JSON
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Target selector */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium mr-1">Design for:</span>
        {(
          [
            { id: "iphone", label: "iPhone", icon: Smartphone },
            { id: "ipad", label: "iPad", icon: Tablet },
            { id: "both", label: "Both", icon: null },
          ] as const
        ).map((opt) => {
          const Icon = opt.icon;
          return (
            <Button
              key={opt.id}
              size="sm"
              variant={target === opt.id ? "default" : "outline"}
              onClick={() => changeTarget(opt.id)}
              className="gap-1"
            >
              {Icon && <Icon className="h-4 w-4" />}
              {opt.label}
            </Button>
          );
        })}

        {target === "both" && (
          <div className="ml-auto flex gap-1">
            <Button
              size="sm"
              variant={activeFf === "iphone" ? "secondary" : "ghost"}
              onClick={() => { setActiveFf("iphone"); setPageIndex(0); }}
              className="gap-1"
            >
              <Smartphone className="h-4 w-4" /> iPhone
            </Button>
            <Button
              size="sm"
              variant={activeFf === "ipad" ? "secondary" : "ghost"}
              onClick={() => { setActiveFf("ipad"); setPageIndex(0); }}
              className="gap-1"
            >
              <Tablet className="h-4 w-4" /> iPad
            </Button>
          </div>
        )}
      </div>

      {target === "both" && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            The iPhone variant is exported as the primary layout for compatibility. When you save the configuration,
            you'll be prompted to also create a sibling iPad-specific policy from your iPad design.
          </AlertDescription>
        </Alert>
      )}

      {summary.errors > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            {summary.errors} error{summary.errors === 1 ? "" : "s"} must be fixed before deploying.
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              {issues.filter((i) => i.level === "error").slice(0, 5).map((i, idx) => (
                <li key={idx}>{i.message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
      {summary.errors === 0 && summary.warnings > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            {summary.warnings} warning{summary.warnings === 1 ? "" : "s"}.
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              {issues.filter((i) => i.level === "warning").slice(0, 5).map((i, idx) => (
                <li key={idx}>{i.message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {view === "json" ? (
        <JsonView value={value} />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
            <div className={cn(
              "rounded-lg border bg-muted/20 p-4 overflow-auto",
              "min-h-[640px]"
            )}>
              <DeviceCanvas
                ff={activeFf}
                layout={layout}
                pageIndex={pageIndex}
                onPageChange={setPageIndex}
                onAddPage={addPage}
                onRemovePage={removePage}
                onSelect={setSelected}
                selected={selected}
                onOpenFolder={(i) => setOpenFolderIdx(i)}
                onWebClipIconKindResolved={handleWebClipIconKindResolved}
              />

              {selected && (
                <div className="mt-3 flex items-center justify-center gap-2">
                  <Button size="sm" variant="outline" onClick={removeSelected}>
                    Remove icon
                  </Button>
                  {selected.kind === "page" &&
                    layout.Pages[selected.pageIndex]?.[selected.itemIndex]?.Type !== "Folder" && (
                      <Button size="sm" variant="outline" onClick={makeFolderFromSelection}>
                        Wrap in folder
                      </Button>
                    )}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="rounded-lg border overflow-hidden h-[640px]">
                <AppLibraryPanel ff={activeFf} ext={ext} onAdd={addItemToActive} />
              </div>
              <ExportSummaryPanel ext={ext} issuesByFf={issuesByFf} />
              <DropRulesPanel ff={activeFf} />
            </div>
          </div>
        </DndContext>
      )}

      {folder && openFolderIdx !== null && (
        <FolderModal
          open={openFolderIdx !== null}
          onOpenChange={(v) => { if (!v) setOpenFolderIdx(null); }}
          folder={folder}
          ff={activeFf}
          onWebClipIconKindResolved={handleWebClipIconKindResolved}
          onSave={(next) => {
            const pages = layout.Pages.map((p, i) =>
              i === pageIndex ? p.map((x, j) => (j === openFolderIdx ? next : x)) : p
            );
            writeLayout(activeFf, { ...layout, Pages: pages });
          }}
          onDelete={() => {
            const pages = layout.Pages.map((p, i) =>
              i === pageIndex ? p.filter((_, j) => j !== openFolderIdx) : p
            );
            writeLayout(activeFf, { ...layout, Pages: pages });
            setOpenFolderIdx(null);
          }}
        />
      )}

      <ImportLayoutDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImport={(next) => {
          // Reset view state and let persistence pick up the new arrangement.
          setSelected(null);
          setOpenFolderIdx(null);
          setPageIndex(0);
          onChange(next);
        }}
      />
    </div>
  );
}
